import React, { useContext, useEffect } from 'react'
import styled from 'styled-components'
import media from 'css-in-js-media'

import ArrowLeftIcon from '../svg/caret-left.svg'
import ArrowRightIcon from '../svg/caret-right.svg'

import SwiperCore, { Pagination, Navigation } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'

// install Swiper components
SwiperCore.use([Pagination, Navigation])

// Import Swiper styles
import 'swiper/swiper.scss'

import { Variables } from '../styles/Variables'
import { BodyCopy, H2, H5 } from './Typography'

import CloseIcon from '../svg/close.svg'
import LayoutContext from './LayoutContext'
import { StyledHeading } from './Heading'
import ResponsiveImage from './ResponsiveImage'

import SunIcon from '../svg/sun.svg'
import PhoneIcon from '../svg/phone.svg'
import EmailIcon from '../svg/envelope-simple.svg'
import WebsiteIcon from '../svg/globe-simple.svg'
import LocationIcon from '../svg/map-pin.svg'
import { getHostname } from '../utils'
import { useWeatherData } from '../hooks/WeatherData'

const Arrow = styled.div`
    z-index: 10;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;

    ${media('>=desktop')} {
        width: 24px;
        height: 24px;
    }

    /* &.swiper-button-disabled {
        opacity: 0.5;
    } */
    svg {
        transition: 0.1s transform ease-out;
        will-change: transform;
        width: 100%;
        height: 100%;
    }
    svg path {
        stroke: ${Variables.color.white};
    }
`

const ArrowLeft = styled(Arrow)`
    &:active {
        svg {
            transform: translateX(-3px);
        }
    }
`

const ArrowRight = styled(Arrow)`
    margin-left: 40px;

    &:active {
        svg {
            transform: translateX(3px);
        }
    }
`

const SlideContent = styled.div`
    background: ${Variables.color.pazola};
    padding: 40px 30px;
    box-sizing: border-box;
    opacity: 0;
    transition: 2s opacity, 1.5s transform;
    transform: translateX(30px);
    transition-timing-function: cubic-bezier(0.29, 0.46, 0.1, 1);
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;

    ${media('>=desktop')} {
        width: 50%;
        padding: 200px 60px 80px;
    }

    ${H2} {
        font-family: ${Variables.font.title};
        font-weight: 300;
        font-size: 38px;
        line-height: 44px;
        color: ${Variables.color.white};     
        ${media('<desktop')} {
            font-size: 24px;
            line-height: 30px;
        }   
    }

    ${BodyCopy} {
        color: ${Variables.color.white};
        flex-grow: 1;
        margin: 20px 0;
        font-size: 18px;
        line-height: 22px;
        
        ${media('<desktop')} {
            font-size: 16px;
            line-height: 20px;
        }

        h4 {
            font-size: 36px;
            line-height: 47px;
            margin: 0;
            font-weight: normal;

            + p {
                margin-top: 21px;
            }
        }
    }
`

const Slider = styled(Swiper)`
    padding: 0;
    height: 100%;
    width: 100%;

    .swiper-wrapper {
        transition-timing-function: cubic-bezier(0.29, 0.46, 0.1, 1);
    }

    ${H5} {
        font-family: ${Variables.font.body};
        font-size: 14px;
        line-height: 120%;
        letter-spacing: 0.0025em;
        text-transform: none;
    }

    .swiper-slide-active ${SlideContent} {
        opacity: 1;
        transform: translateX(0px);
    }
`

const CloseButton = styled.div`
    position: absolute;
    right: 40px;
    top: 40px;
    z-index: 10;
    cursor: pointer;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;

    :before {
        content: '';
        background: #fff;
        width: 42px;
        height: 42px;
        position: absolute;
        top: 50%;
        left: 50%;
        z-index: 0;
        border-radius: 50px;
        transition: 0.6s transform cubic-bezier(0.3, 0.51, 0, 1.08);

        transform: translate3d(-50%, -50%, 0) scale3d(0, 0, 0);
    }

    svg {
        position: relative;
        z-index: 1;
        vertical-align: top;

        path {
            transition: 0.3s stroke ease-out;

            stroke: ${Variables.color.warmGrey};
        }
    }

    :hover {
        svg line{
            stroke: ${Variables.color.pazola};
        }
        :before {
            transform: translate3d(-50%, -50%, 0) scale3d(1, 1, 1);
        }
    }

    ${media('<desktop')} {
        display: none;
    }
`

const MapSlideWrapper = styled.div`
    display: flex;
    background: ${Variables.color.cocoa};
    color: white;
    height: 100%;

    ${media('<desktop')} {
        flex-direction: column;
    }
`

const ImageWrap = styled.div`
    ${media('>=desktop')} {
        width: 50%;
        height: 100%;
    }

    > div,
    > div > div {
        height: 100%;
    }

    .gatsby-image-wrapper {
        max-height: 100%;
    }

    img {
        vertical-align: top;
        transform: translateZ(1px);
    }
`

const MapHeading = styled.div`
    background: white;

    h2,
    ${BodyCopy} {
        ${media('<desktop')} {
            text-align: left;
        }
    }
    ${StyledHeading} {
        ${media('<desktop')} {
            padding-top: 80px;
            padding-bottom: 80px;
        }
    }
`

const SliderControls = styled.div`
    position: absolute;
    ${media('>=desktop')} {
        left: 50%;
        right: 0;
        bottom: 0;
        top: 0;
    }
    ${media('<desktop')} {
        top: 0;
        z-index: 10;
        right: 0;
        left: 0;
        height: 0;
        padding-bottom: 84.266%;
        pointer-events: none;
    }
`

const Arrows = styled.div`
    display: flex;
    position: absolute;
    ${media('>=desktop')} {
        left: 60px;
        top: 40px;
    }

    ${media('<desktop')} {
        bottom: 0;
        right: 0;
        background: ${Variables.color.pazola};
        padding: 10px 30px;
        pointer-events: all;
    }
`

const Icons = styled.div`
    display: flex;
    ${media('<desktop')} {
        justify-content: space-between;
        margin: 0 -40px 40px;
    }
    ${media('>=desktop')} {
        margin: 0 -40px 80px;
    }
`

const IconsItem = styled.div`
    display: flex;
    align-items: center;
    text-align: center;
    margin: 0 40px;

    svg path {
        fill: ${Variables.color.white};
    }

    span {
        font-size: 16px;
        line-height: 26px;
        text-align: center;
        letter-spacing: 0.0025em;
        color: ${Variables.color.white};
        margin-left: 11px;
    }
`

const Info = styled.div`
    display: flex;
    flex-direction: column;
    ${media('<desktop')} {
        margin-top: 40px;
    }

    ${media('>=desktop')} {
        margin-bottom: -20px;
    }
`

const InfoItem = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 20px;

    svg {
        width: 30px;
        height: 30px;
        margin-right: 10px;
    }

    span {
        font-size: 16px;
        line-height: 26px;
        letter-spacing: 0.0025em;
        color: ${Variables.color.white};
    }
`

const Location = styled.div`
    display: flex;
    align-items: center;
    ${media('>=desktop')} {
        margin-top: 40px;
    }

    svg {
        margin-right: 10px;
        width: 17px;
        height: 25px;

        path {
            fill: ${Variables.color.white};
        }
    }

    span {
        font-size: 16px;
        line-height: 26px;
        letter-spacing: 0.0025em;
        color: ${Variables.color.white};
    }
`

const InteractiveMapSlider = ({ data, isMap, onSlideChange }) => {
    const { isMapOpen, setMapOpen, ActiveMapSlide, setActiveMapSlide } = useContext(LayoutContext)
    const { title, copy, image, slides, id } = data
    const weatherData = useWeatherData()

    const deactiveMap = () => {
        setMapOpen(false)
    }

    useEffect(() => {
        if (ActiveMapSlide) {
            ActiveMapSlide.map((slide, index) => slide.update())
        }
    }, [ActiveMapSlide, isMapOpen])

    return (
        <>
            {data && (
                <>
                    <Slider
                        navigation={{
                            prevEl: '.MapPrev',
                            nextEl: '.MapNext',
                        }}
                        loop={true}
                        speed={1000}
                        onSlideChange={onSlideChange}
                        slidesPerView="1"
                        onSwiper={(instance) => {                            
                            if (isMap) {
                                setActiveMapSlide((ActiveMapSlide) => [...ActiveMapSlide, instance])
                            }
                        }}                        
                    >
                        {slides &&
                            slides.map((slide, index) => {
                                return (
                                    <SwiperSlide key={index}>
                                        <MapSlideWrapper>
                                            <ImageWrap>
                                                <ResponsiveImage image={slide.image} />
                                            </ImageWrap>
                                            <SlideContent>
                                                <Icons>
                                                        <IconsItem>
                                                            <SunIcon />
                                                            <span>{Math.round(weatherData.main.temp)}°C</span>
                                                        </IconsItem>
                                                </Icons>
                                                <H2>{slide.title}</H2>
                                                <BodyCopy
                                                    dangerouslySetInnerHTML={{
                                                        __html: slide.copy.childMarkdownRemark.html,
                                                    }}
                                                />
                                                <Info>
                                                    {slide.phone && (
                                                        <InfoItem>
                                                            <PhoneIcon />
                                                            <a href={`tel:` + slide.phone}>
                                                                <span>{slide.phone}</span>
                                                            </a>
                                                        </InfoItem>
                                                    )}
                                                    {slide.email && (
                                                        <InfoItem>
                                                            <EmailIcon />
                                                            <a href={`mailto:` + slide.email}>
                                                                <span>{slide.email}</span>
                                                            </a>
                                                        </InfoItem>
                                                    )}
                                                    {slide.website && (
                                                        <InfoItem>
                                                            <WebsiteIcon />
                                                            <a href={slide.website} target="_blank">
                                                                <span>{getHostname(slide.website)}</span>
                                                            </a>
                                                        </InfoItem>
                                                    )}
                                                </Info>
                                                {slide.location.lat && slide.location.lon && (
                                                    <Location>
                                                        <LocationIcon />
                                                        <span>
                                                            {slide.location.lat}° N, {slide.location.lon}° E
                                                        </span>
                                                    </Location>
                                                )}
                                            </SlideContent>
                                        </MapSlideWrapper>
                                    </SwiperSlide>
                                )
                            })}
                        <SliderControls>
                            <Arrows>
                                <ArrowLeft className="MapPrev">
                                    <ArrowLeftIcon />
                                </ArrowLeft>
                                <ArrowRight className="MapNext">
                                    <ArrowRightIcon />
                                </ArrowRight>
                            </Arrows>

                            <CloseButton
                                onClick={() => {
                                    deactiveMap()
                                }}
                            >
                                <CloseIcon />
                            </CloseButton>
                        </SliderControls>
                    </Slider>
                </>
            )}
        </>
    )
}

export default InteractiveMapSlider
